/**
 * CDID Settings tab — runtime configuration for the unattended sessions.
 *
 * Served by /admin/autobuild/settings, stored in Firestore. The GitHub Actions
 * runner reads the SAME doc at session start (scripts/read-autobuild-settings.cjs),
 * so what is shown here is what the next session will actually do.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ORCHESTRATOR_CANDIDATES, type OrchestratorAccount } from '@a7y/types';
import { api } from '@/lib/api';
import { fmtLocal, fmtUtc } from './shared';

const ACCOUNT_EMAIL: Record<OrchestratorAccount, string> = {
  team: 'team@apptonomy.ai',
};

/** Why an account might be a poor choice — surfaced rather than enforced. */
const ACCOUNT_NOTE: Partial<Record<OrchestratorAccount, string>> = {
  team: 'Dedicated to orchestration. Since 2026-08-26 it is the only account the runtime accepts.',
};

export function CdidSettingsTab() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['autobuild-settings'],
    queryFn: () => api.getAutobuildSettings(),
  });
  const [error, setError] = useState<string | null>(null);

  const mutate = useMutation({
    mutationFn: (account: OrchestratorAccount) => api.setOrchestratorAccount(account),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['autobuild-settings'] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
  });

  const current = settings.data?.orchestratorAccount;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 border-b border-zinc-100 dark:border-zinc-800">
          Orchestrator account
        </div>

        <div className="p-3 space-y-3">
          <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
            The Claude account every session kind runs on — planning, Karen, verification,
            landing and reporting. It is <strong>pinned to team@</strong>, not chosen per run,
            so that orchestration never competes with implementation for the same 5-hour
            window. team@ is removed from the worker driver pool and the worker
            implementation view automatically, and the runtime honours no other value. This
            reserves the <em>role</em>, not a slot — team@ still runs as many concurrent
            orchestrators as its quota allows.
          </p>

          {settings.isLoading && <div className="text-xs text-zinc-500">Loading…</div>}
          {settings.isError && (
            <div className="text-xs text-red-600 dark:text-red-400">
              Could not load settings: {(settings.error as Error)?.message}
            </div>
          )}

          {current && (
            <div className="space-y-1.5">
              {ORCHESTRATOR_CANDIDATES.map((account) => {
                const selected = account === current;
                return (
                  <label
                    key={account}
                    className={`flex items-start gap-3 rounded-md border p-2.5 cursor-pointer transition-colors ${
                      selected
                        ? 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/10'
                        : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="orchestrator"
                      className="mt-0.5"
                      checked={selected}
                      disabled={mutate.isPending}
                      onChange={() => mutate.mutate(account)}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {ACCOUNT_EMAIL[account]}
                        {selected && (
                          <span className="ml-2 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                            Current
                          </span>
                        )}
                      </span>
                      {ACCOUNT_NOTE[account] && (
                        <span className="block text-[11px] text-zinc-500 dark:text-zinc-400">
                          {ACCOUNT_NOTE[account]}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}

          <p className="text-[11px] text-zinc-400">
            {mutate.isPending
              ? 'Saving…'
              : settings.data?.updatedAt
                ? `Last changed ${fmtLocal(settings.data.updatedAt)} by ${settings.data.updatedBy ?? 'unknown'}`
                : 'Never changed — showing the built-in default.'}
            {settings.data?.updatedAt && (
              <span className="ml-1" title={`${fmtUtc(settings.data.updatedAt)} UTC`} />
            )}
          </p>
          <p className="text-[11px] text-zinc-400">
            Takes effect on the next session start; a session already running keeps the account
            it began on.
          </p>
        </div>
      </div>
    </div>
  );
}
